import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import dayjs from "utils/dayjs";
import { Alert, Button, Divider, Typography } from "@mui/material";
import { Avatar, Card, Chip, DataRow, PageHeader, toast } from "@outpave/ds";
import { HugeiconsIcon } from "@hugeicons/react";
import { Edit04Icon, PlugSocketIcon } from "@hugeicons-pro/core-stroke-rounded";
import { toastMutationError } from "utils/toastMutationError";
import CardProgramStatus from "enums/CardProgramStatus";
import PlatformIntegrationCategory from "enums/PlatformIntegrationCategory";
import {
  PlatformIntegrationCheck,
  sectionApplies,
  usePlatformIntegration,
  usePlatformIntegrationTemplates,
} from "hooks/usePlatformIntegrations";
import { logoLink } from "components/common/RenderMerchantDisplay";
import { useColorScheme } from "context/ColorSchemeContext";
import { useNavBreadcrumbs } from "hooks/useNavBreadcrumbs";
import { CATEGORY_LABEL_KEY } from "utils/platformIntegrations/integrationLabels";
import { INTEGRATIONS_PATH } from "utils/platformIntegrations/integrationPaths";
import { EMPTY } from "components/common/detailRows";
import IdNotFoundPage from "components/common/IdNotFoundPage";
import { Columns, Inline, Stack } from "components/layout/reusableLayouts";
import IntegrationSectionCard from "components/platform/integrations/IntegrationSectionCard";
import IntegrationCardPrograms from "components/platform/integrations/IntegrationCardPrograms";
import IntegrationFormDrawer from "components/platform/integrations/IntegrationFormDrawer";
import IntegrationDetailsDrawer from "components/platform/integrations/IntegrationDetailsDrawer";

/** One connected provider: its template's sections, its card programs, and whether it is in use. */
const PlatformIntegrationDetails: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isDark } = useColorScheme();
  const { integrationId = "" } = useParams<{ integrationId: string }>();
  const {
    integration,
    loading,
    saveIntegration,
    checkIntegration,
    refreshCardProducts,
    refreshing,
    updateCardProduct,
  } = usePlatformIntegration(integrationId);
  const { getTemplate } = usePlatformIntegrationTemplates();
  const [editingSection, setEditingSection] = useState<string | null>(null);
  const [editingDetails, setEditingDetails] = useState(false);
  const [check, setCheck] = useState<PlatformIntegrationCheck | null>(null);
  const [checking, setChecking] = useState(false);

  const template = getTemplate(integration?.category, integration?.provider);
  const backToIntegrations = () => navigate(INTEGRATIONS_PATH);
  // The settings step from the nav, then the marketplace, then this integration.
  const parentCrumbs = useNavBreadcrumbs(t("Integrations")).slice(0, -1);

  if (!integration || !template) {
    return (
      <IdNotFoundPage
        title={t("Integration")}
        isLoading={loading || (!!integration && !template)}
        showSkeleton
      />
    );
  }

  const provider = template.label;
  const category = t(CATEGORY_LABEL_KEY[integration.category]);
  const active = integration.status !== CardProgramStatus.INACTIVE;
  const fieldLabels = new Map(
    template.sections
      .flatMap((section) => section.fields)
      .map((field) => [field.key, t(field.labelKey)]),
  );

  const runCheck = async () => {
    setChecking(true);
    try {
      setCheck(await checkIntegration({ id: integration.id }));
    } catch (error: unknown) {
      setCheck({
        ok: false,
        message: errorMessage(error, t("Please try again")),
      });
    } finally {
      setChecking(false);
    }
  };

  const makeDefault = async () => {
    try {
      await saveIntegration({ id: integration.id, isDefault: true });
      toast.success(
        t("IntegrationMadeDefaultToast", {
          name: integration.name,
          category,
          interpolation: { escapeValue: false },
        }),
      );
    } catch (error: unknown) {
      toastMutationError(error, t("IntegrationSaveError"));
    }
  };

  return (
    <Stack gap="var(--space-5)" sx={{ padding: "var(--space-6)" }}>
      <PageHeader
        sx={{ gap: "var(--space-5)" }}
        breadcrumbs={[
          ...parentCrumbs,
          {
            label: t("Integrations"),
            href: INTEGRATIONS_PATH,
            onClick: backToIntegrations,
          },
          { label: integration.name },
        ]}
        back={{ label: t("Back to integrations"), onClick: backToIntegrations }}
        title={
          <Inline gap="var(--space-4)" align="center" wrap={false}>
            <Avatar
              src={logoLink(
                template.domain,
                "icon",
                "lettermark",
                isDark ? "dark" : "light",
                48,
              )}
              name={provider}
              alt={provider}
              size="xl"
              variant="rounded"
            />
            <Typography variant="h3" component="span">
              {integration.name}
            </Typography>
          </Inline>
        }
        meta={
          <Inline gap="var(--space-2)">
            {integration.isDefault && (
              <Chip size="sm" tone="positive" label={t("Default")} />
            )}
            <Chip
              size="sm"
              dot
              tone={active ? "positive" : "neutral"}
              label={active ? t("Active") : t("Inactive")}
            />
          </Inline>
        }
        actions={
          <>
            <Button
              variant="default-outlined"
              startIcon={<HugeiconsIcon icon={PlugSocketIcon} size={16} />}
              onClick={() => void runCheck()}
              disabled={checking}
            >
              {t("Test connection")}
            </Button>
            {!integration.isDefault &&
              active &&
              integration.category !==
                PlatformIntegrationCategory.ACQUIRING && (
                <Button
                  variant="default-outlined"
                  onClick={() => void makeDefault()}
                >
                  {t("Make default")}
                </Button>
              )}
            <Button
              variant="default-outlined"
              startIcon={<HugeiconsIcon icon={Edit04Icon} size={16} />}
              onClick={() => setEditingDetails(true)}
            >
              {t("Edit details")}
            </Button>
          </>
        }
      />

      {integration.missingFields.length > 0 && (
        <Alert severity="warning" role="status">
          {t("IntegrationMissingFields", {
            fields: integration.missingFields
              .map((key) => fieldLabels.get(key) ?? key)
              .join(", "),
            interpolation: { escapeValue: false },
          })}
        </Alert>
      )}
      {check && (
        <Alert
          severity={check.ok ? "success" : "error"}
          role={check.ok ? "status" : "alert"}
          onClose={() => setCheck(null)}
        >
          {check.ok
            ? t("ConnectionCheckPassed", { provider })
            : check.message || t("ConnectionCheckFailed")}
        </Alert>
      )}

      <Columns
        count={{ xs: 1, lg: "minmax(0, 1.6fr) minmax(0, 1fr)" }}
        gap="var(--space-5)"
        sx={{ alignItems: "start" }}
      >
        <Stack gap="var(--space-5)">
          {template.hasCardProducts && (
            <IntegrationCardPrograms
              integration={integration}
              providerName={provider}
              refreshing={refreshing}
              onRefresh={refreshCardProducts}
              onUpdate={updateCardProduct}
            />
          )}
          {template.sections
            .filter((section) => sectionApplies(section, integration))
            .map((section) => (
              <IntegrationSectionCard
                key={section.key}
                integration={integration}
                section={section}
                onEdit={() => setEditingSection(section.key)}
              />
            ))}
        </Stack>

        <Card variant="outlined" title={t("Details")}>
          <Stack gap="var(--space-0)">
            {[
              { label: t("Provider"), value: provider },
              { label: t("Category"), value: category },
              {
                label: t("Default"),
                value: integration.isDefault ? t("Yes") : t("No"),
              },
              {
                label: t("Created"),
                value: integration.createdAt
                  ? dayjs(integration.createdAt).format("ll")
                  : EMPTY,
              },
              {
                label: t("Last updated"),
                value: integration.updatedAt
                  ? dayjs(integration.updatedAt).format("lll")
                  : EMPTY,
              },
            ].map(({ label, value }, index) => (
              <React.Fragment key={label}>
                {index > 0 && <Divider />}
                <DataRow label={label} primaryTrailing={value} />
              </React.Fragment>
            ))}
          </Stack>
        </Card>
      </Columns>

      <IntegrationFormDrawer
        open={!!editingSection}
        template={template}
        integration={integration}
        sectionKey={editingSection}
        onClose={() => setEditingSection(null)}
        onSave={saveIntegration}
        onCheck={checkIntegration}
      />
      <IntegrationDetailsDrawer
        open={editingDetails}
        integration={integration}
        onClose={() => setEditingDetails(false)}
        onSave={saveIntegration}
      />
    </Stack>
  );
};

const errorMessage = (error: unknown, fallback: string) =>
  error instanceof Error ? error.message : fallback;

export default PlatformIntegrationDetails;
